import {useCallback} from 'react';
import {pick} from 'lodash';

import {
  PolicyFilter,
  PolicyStatusType,
  PeriodType,
  InclusionDateType,
  PolicyAmountType,
} from 'modules/decisionsNext/api/types';

import {cleanApolloResponse} from 'utils/misc';
import {useFeatureFlag} from 'core/hooks/authHooks';
import {FeatureFlags} from 'core/constants/featureFlags';
import {cleanEmptyValuesStrict} from 'utils/cleanEmptyValues';
import {globalFilterKeysMap, formatPolicyFilterPeriod} from 'features/GlobalFilterProvider/utils/formatters';

// Sync with modules/decisionsNext/components/GlobalFilter/store/filter/formatters.ts
export const useFormatGlobalFilter = () => {
  const withPolicyStatusFilter = useFeatureFlag(FeatureFlags.PolicyStatusFilter);
  const withClientTagsFilter = useFeatureFlag(FeatureFlags.ClientTagsFilter);
  const withPolicyTagsFilter = useFeatureFlag(FeatureFlags.PolicyTagsFilter);
  const withPolicySourceCodes = useFeatureFlag(FeatureFlags.PolicySourceCodes);
  const withAmsProducerCodes = useFeatureFlag(FeatureFlags.AmsProducerCodes);
  const withAccountExecutives = useFeatureFlag(FeatureFlags.AccountExecutives);
  const withAnnualizedAmountType = useFeatureFlag(FeatureFlags.AnnualizedValues);

  return useCallback(
    (filter: PolicyFilter) => {
      filter = pick(filter, Object.keys(globalFilterKeysMap));
      filter = {
        ...filter,
        period: formatPolicyFilterPeriod(filter.period),
      };
      filter =
        !withAnnualizedAmountType && filter.period?.amountType === PolicyAmountType.AnnualizedWritten
          ? {
              ...filter,
              period: {
                ...filter.period,
                amountType: PolicyAmountType.Written,
              },
            }
          : filter;

      return cleanEmptyValuesStrict(
        cleanApolloResponse({
          ...filter,
          policyStatusType: withPolicyStatusFilter ? filter.policyStatusType : undefined,
          policyStatuses:
            !withPolicyStatusFilter && filter.policyStatusType === PolicyStatusType.Ams
              ? undefined
              : filter.policyStatuses,
          clientAgencyCodes: withClientTagsFilter ? filter.clientAgencyCodes : undefined,
          retailAgencyCodes: undefined,
          policySourcesFilter: withPolicyTagsFilter && withPolicySourceCodes ? filter.policySourcesFilter : undefined,
          policyAgencyCodes: withPolicyTagsFilter ? filter.policyAgencyCodes : undefined,
          userFilters: {
            ...filter.userFilters,
            accountExecutiveIdsAndSourceCodes: withAccountExecutives
              ? filter.userFilters?.accountExecutiveIdsAndSourceCodes?.map(({userId, sourceCodes}) => {
                  return {
                    userId,
                    sourceCodes: withAmsProducerCodes ? sourceCodes : undefined,
                  };
                })
              : undefined,
            producerIdsAndSourceCodes: filter.userFilters?.producerIdsAndSourceCodes?.map(({userId, sourceCodes}) => {
              return {
                userId,
                sourceCodes: withAmsProducerCodes ? sourceCodes : undefined,
              };
            }),
            accountManagerIdsAndSourceCodes: filter.userFilters?.accountManagerIdsAndSourceCodes?.map(
              ({userId, sourceCodes}) => {
                return {
                  userId,
                  sourceCodes: withAmsProducerCodes ? sourceCodes : undefined,
                };
              }
            ),
          },
          additionalPeriods:
            filter.period?.type !== PeriodType.InForce &&
            filter.period?.inclusionDateType !== InclusionDateType.Transaction
              ? filter.additionalPeriods?.map(formatPolicyFilterPeriod)
              : undefined,
        })
      ) as PolicyFilter;
    },
    [
      withAccountExecutives,
      withAmsProducerCodes,
      withAnnualizedAmountType,
      withClientTagsFilter,
      withPolicySourceCodes,
      withPolicyStatusFilter,
      withPolicyTagsFilter,
    ]
  );
};
